import React from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { INR } from "@/lib/constants";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { TrendingUp, Repeat, UserPlus } from "lucide-react";

const VOLT = "#CCFF00";
const BLUE = "#3B82F6";

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const r = payload.find((p) => p.dataKey === "renewals")?.value || 0;
  const n = payload.find((p) => p.dataKey === "new_leads")?.value || 0;
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-3 text-xs">
      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-2">{label}</div>
      <div className="flex items-center gap-2 mb-1"><div className="w-2 h-2" style={{ background: VOLT }} /><span className="text-zinc-300">Renewals</span><span className="ml-auto font-bold text-volt">{INR(r)}</span></div>
      <div className="flex items-center gap-2"><div className="w-2 h-2" style={{ background: BLUE }} /><span className="text-zinc-300">New Leads</span><span className="ml-auto font-bold text-blue-400">{INR(n)}</span></div>
      <div className="border-t border-zinc-800 mt-2 pt-2 flex items-center gap-2 font-bold"><span className="text-zinc-300">Total</span><span className="ml-auto text-white">{INR(r + n)}</span></div>
    </div>
  );
}

export default function PipelineProjectionChart() {
  const { user, activeBranchId } = useAuth();
  const enabled = user?.role === "SUPER_ADMIN" || user?.role === "ADMIN";
  const params = new URLSearchParams({ months: "12" });
  if (activeBranchId) params.set("branch_id", activeBranchId);
  const { data } = useQuery({
    queryKey: ["pipeline-projection", activeBranchId],
    queryFn: async () => (await apiClient.get(`/finance/pipeline-projection?${params.toString()}`)).data,
    enabled,
  });
  if (!enabled || !data) return null;
  // Short labels like 'Aug 26' for X axis
  const rows = data.months.map((m) => {
    const [y, mo] = m.month.split("-");
    const short = new Date(Number(y), Number(mo) - 1, 1).toLocaleString("en-IN", { month: "short" });
    return { ...m, label: `${short} ${y.slice(2)}` };
  });
  return (
    <Panel data-testid="pipeline-projection-panel">
      <PanelHeader
        title="Projected Sales · Next 12 Months"
        subtitle="Renewals (from active memberships expiring) + weighted new-lead pipeline"
        action={
          <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.16em]">
            <div className="flex items-center gap-1"><Repeat size={12} className="text-volt" /><span className="text-zinc-400">Renewals</span><span className="text-volt font-bold">{INR(data.total_renewals)}</span></div>
            <div className="flex items-center gap-1"><UserPlus size={12} className="text-blue-400" /><span className="text-zinc-400">New Leads</span><span className="text-blue-400 font-bold">{INR(data.total_new_leads)}</span></div>
            <div className="flex items-center gap-1 pl-3 border-l border-zinc-800"><TrendingUp size={12} className="text-white" /><span className="text-zinc-400">Total</span><span className="text-white font-bold">{INR(data.grand_total)}</span></div>
          </div>
        }
      />
      <div style={{ width: "100%", height: 320 }} data-testid="pipeline-chart">
        <ResponsiveContainer>
          <BarChart data={rows} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "#71717A", fontSize: 11 }} axisLine={{ stroke: "#27272a" }} tickLine={false} />
            <YAxis tick={{ fill: "#71717A", fontSize: 11 }} axisLine={{ stroke: "#27272a" }} tickLine={false}
              tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
            <Legend
              wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#a1a1aa" }}
              iconType="square"
              formatter={(v) => v === "renewals" ? "Renewals" : "New Leads"}
            />
            <Bar dataKey="renewals" stackId="a" fill={VOLT} radius={[0, 0, 0, 0]} />
            <Bar dataKey="new_leads" stackId="a" fill={BLUE} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
}
